import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { FlagDefinition } from '@zag/feature-flags/common';
import { ZagButton, ZagIcon } from '@zag/ui';
import { FlagDefinitionCardComponent } from '../flag-definition-card/flag-definition-card.component';

/**
 * Catalog view for feature-flag definitions — mirrors the shape of
 * `<zag-licence-definition-list>` from `@zag/licensing/ui` but drops
 * the card/list view toggle and sort options (flag catalogs are
 * typically small enough to browse as a card grid). The host handles
 * edit navigation, creation, and reload after mutation.
 */
@Component({
  selector: 'zag-flag-definition-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlagDefinitionCardComponent, TranslatePipe, ZagButton, ZagIcon],
  template: `
    <div class="wrap">
      <header class="wrap__header">
        <div>
          <h2 class="wrap__title">{{ 'control.featureFlags.definitions.title' | translate }}</h2>
          <p class="wrap__subtitle">{{ 'control.featureFlags.definitions.subtitle' | translate }}</p>
        </div>
        <button type="button" zagButton="basic" zagType="primary" zagSize="sm" (click)="create.emit()">
          <span zagIcon="add" aria-hidden="true"></span>
          {{ 'control.featureFlags.definitions.createButton' | translate }}
        </button>
      </header>

      @if (loading()) {
        <div class="loading" role="status" aria-live="polite">
          {{ 'common.loading' | translate }}
        </div>
      } @else if (items().length === 0) {
        <div class="empty">
          <p class="empty__title">{{ 'control.featureFlags.definitions.empty.title' | translate }}</p>
          <p class="empty__hint">{{ 'control.featureFlags.definitions.empty.hint' | translate }}</p>
        </div>
      } @else {
        <ul class="cards" role="list">
          @for (definition of items(); track definition.key) {
            <li>
              <zag-flag-definition-card [definition]="definition" (edit)="edit.emit($event)" />
            </li>
          }
        </ul>
      }
    </div>
  `,
  styleUrl: './flag-definition-list.component.scss',
})
export class FlagDefinitionListComponent {
  readonly items = input<readonly FlagDefinition[]>([]);
  readonly loading = input<boolean>(false);

  readonly create = output<void>();
  readonly edit = output<FlagDefinition>();
}
